import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import type { MuiTelInputInfo } from "mui-tel-input";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
// import StyledTextField from "../../styled-components/StyledTextField";
import styles from "../EntityEditor.styles";
import StyledPhoneInput from "../../styled-components/StyledPhoneInput";
import { useDispatch } from "react-redux";
import { LatinTextField } from '../../latin-text-field/LatinTextField';
import AlertService from '../../../services/notification.service';
import { PHONE_RE } from '../../../constants/patterns-constants';

export type EntityPhoneNumbersBlockHandle = {
  reset: () => void;
}

interface EntityPhoneNumbersBlockProps  {
  phoneNumbers?: any,
  onChange?: any,

}

type PhoneItem = {
  id?: number;
  entity_id?: string;
  type: string;
  number: string;
}

export const initialErrorPhoneNumberState = {
  type: false,
  number: false,
  countryCallingCode: false,
}

const initialEditModeState = {
  isEdit: false,
  index: -1,
};

const initialPhoneNumberState = {
  type: "",
  number: "",
  countryCallingCode: "",
};

const initialPhoneNumberDetails = {
  type: "",
  number: "",
  countryCallingCode: "",
  index: -1,
  id: "",
  modified: false,
};

const isDuplicate = (phoneNumber: string, list: Array<PhoneItem>, index?: number) => {
  return list.some((item, idx) => {
    if (index !== undefined && idx === index) {
      return false;
    }
    return phoneNumber === item.number;
  });
};

const HELPER_H = 20;

const compact = (s: string) => (s || "").replace(/[^\d+]/g, "");
const isEmptyPhoneNumber = (raw: string): boolean => {
   const v = compact(raw);
   if (v === "" || v === "+") return true;
   return  /^\+\d{1,3}$/.test(v);
};

const EntityPhoneNumbersBlock = React.forwardRef<EntityPhoneNumbersBlockHandle, EntityPhoneNumbersBlockProps>(({ phoneNumbers, onChange }, ref) => {
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(initialEditModeState);
  const [entityPhoneNumber, setEntityPhoneNumber] = useState(
    initialPhoneNumberState
  );
  const [entityPhoneNumbersList, setEntityPhoneNumbersList] = useState<PhoneItem[]>([]);
  const [editedPhoneNumberDetails, setEditedPhoneNumberDetails] = useState(
    initialPhoneNumberDetails
  );
  const [phoneNumbersForDelete, setPhoneNumberForDelete] = useState([]);
  const [errorFields, setErrorFields] = useState(initialErrorPhoneNumberState);
  const [errorEditFields, setErrorEditFields] = useState(initialErrorPhoneNumberState);

  useEffect(() => {
    setEntityPhoneNumbersList(phoneNumbers);
  }, [dispatch, phoneNumbers]);

  const handleClearForm = () => {
    /** clear all fields and error statuses **/
    setEditedPhoneNumberDetails(initialPhoneNumberDetails);
    setEntityPhoneNumber(initialPhoneNumberState);
    setEditMode(initialEditModeState);
    setEntityPhoneNumbersList([]);
    setPhoneNumberForDelete([]);
    setErrorFields(initialErrorPhoneNumberState);
    setErrorEditFields(initialErrorPhoneNumberState);
  };
  const ClearForm = useCallback(() => {
    handleClearForm();
  }, []);
  useImperativeHandle(ref, () => ({
    reset: ClearForm
  }), [ClearForm]);

  const handleBlur = (key: string) => {
    if (isEmptyPhoneNumber(entityPhoneNumber?.number) && key === "number") {
       setErrorFields(prev => ({ ...prev, [key]: false}));
    } else {
      setErrorFields(prev => ({ ...prev, [key]: !PHONE_RE.test(entityPhoneNumber?.number)}))
    }
  }
  const handleChange = (
    _name: string,
    key: string,
    event: unknown,
    value: MuiTelInputInfo | null,
  ) => {
    if (key === "number") {
      const numberValue = value?.numberValue ?? "";
      const countryCallingCode = value?.countryCallingCode ?? "";
      setErrorFields(prev => ({ ...prev, [key]: numberValue ? !PHONE_RE.test(numberValue) : false
       }));
      return setEntityPhoneNumber(prev => ({
        ...prev,
        [key]: numberValue,
        countryCallingCode,
      }));
    }
    const e = event as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    return setEntityPhoneNumber(prev =>({
      ...prev,
      [key]: e.target.value,
    }));
  };

  const handleBlurEdit = (key: string) => {
    if (isEmptyPhoneNumber(editedPhoneNumberDetails?.number) && key === "number") {
      setErrorEditFields(prev => ({ ...prev, [key]: true}));
    } else {
      setErrorEditFields(prev => ({ ...prev, [key]: !PHONE_RE.test(editedPhoneNumberDetails?.number)}))
    }
  }

  const handleChangeItem = (
    fieldName: string,
    selectedIndex: number,
    event: unknown,
    value: MuiTelInputInfo | null,
  ) => {
    if (fieldName === "number") {
      const numberValue = value?.numberValue ?? "";
      const countryCallingCode = value?.countryCallingCode ?? "";
      setErrorEditFields(prev => ({ ...prev, [fieldName]: numberValue ? !PHONE_RE.test(numberValue) : false }));
      return setEditedPhoneNumberDetails(prev => ({
        ...prev,
        [fieldName]: numberValue,
        countryCallingCode,
        index: selectedIndex,
        modified: true,
      }));
    }
    const e = event as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    return setEditedPhoneNumberDetails(prev => ({
      ...prev,
      [fieldName]: e.target.value,
      index: selectedIndex,
      modified: true,
    }));
  };

  const handleDeleteItem = (selectedIndex: number ) => {
    const newStateForDelete = [];
    entityPhoneNumbersList.map((item: any, index) => {
      if (index === selectedIndex && item.id) {
        // @ts-ignore
        return newStateForDelete.push(item);
      } else {
        return item;
      }
    });

    const filteredList = entityPhoneNumbersList.filter(
      (_item, index) => index !== selectedIndex
    );

    const newState = {
      filteredList,
      newStateForDelete,
    };

    setEntityPhoneNumbersList(filteredList);
    setPhoneNumberForDelete([...phoneNumbersForDelete, ...newStateForDelete]);
    onChange("entity_phone_numbers", newState, true);
  };

  const handleSetEditMode = (type: string, selectedIndex: number) => {
    /** managing open/close state for ip item change, save changes by click in ip list **/
    if (type === "open") {
      setErrorEditFields(initialErrorPhoneNumberState);
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      entityPhoneNumbersList.forEach((item: any, index) => {
        if (index === selectedIndex) {
          setEditedPhoneNumberDetails({
            ...item,
            index: selectedIndex,
            type: item.type,
            number: item.number,
            countryCallingCode: item.countryCallingCode,
          });
        }
      });
    }
    if (type === "close") {
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      setErrorEditFields(initialErrorPhoneNumberState);
    }
    if (type === "edit") {
      if (Object.values(errorEditFields).includes(true)) {
        return AlertService.warning("Fields are not valid.")
      }
      if (isDuplicate(editedPhoneNumberDetails.number, entityPhoneNumbersList, selectedIndex)) {
        setErrorEditFields(prev => ({ ...prev, number: true}));
        return AlertService.warning("This phone number is already on the list.");
      }
      const newState = entityPhoneNumbersList.map((item: any, index) => {
        if (
          index === editedPhoneNumberDetails.index &&
          index === selectedIndex
        ) {
          return {
            id: editedPhoneNumberDetails.id,
            type: editedPhoneNumberDetails.type,
            number: editedPhoneNumberDetails.number,
            countryCallingCode: editedPhoneNumberDetails.countryCallingCode,
            modified: editedPhoneNumberDetails.modified,
          };
        } else {
          return item;
        }
      });
      // @ts-ignore
      setEntityPhoneNumbersList(newState);
      onChange("entity_phone_numbers", newState);
      setEditMode({ isEdit: !editMode.isEdit, index: selectedIndex });
      setEditedPhoneNumberDetails(editedPhoneNumberDetails);
    }
  };
  const addNewPhoneNumber = () => {
    if (!isEmptyPhoneNumber(entityPhoneNumber.number) && !Object.values(errorFields).includes(true)) {
      if (isDuplicate(entityPhoneNumber.number, entityPhoneNumbersList)) {
        setErrorFields(prev => ({ ...prev, number: true}));
        return AlertService.warning("This phone number is already on the list.")
      }
      const newState = [
        ...entityPhoneNumbersList,
        {
          type: entityPhoneNumber.type,
          number: entityPhoneNumber.number,
        },
      ];
      // @ts-ignore
      setEntityPhoneNumbersList(newState);
      onChange("entity_phone_numbers", newState);
      handleClearForm();
    } else {
      AlertService.warning('Please enter a valid phone number');
    }
  };

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography component="legend" sx={styles.textField}>
          Phone numbers
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={styles.fieldHolder}>
          <Box sx={styles.fieldBlock}>
            <LatinTextField
              fullWidth
              label="Type"
              type="text"
              name="type"
              errorFields={errorFields.type}
              setErrorFields={setErrorFields}
              helperText={" "}
              slotProps={{
                formHelperText: {
                  sx: {minHeight: HELPER_H}
                }
              }}
              margin="dense"
              value={entityPhoneNumber?.type}
              onChange={(e) =>
                handleChange("entity_phone_numbers", "type", e, null)
              }
            />
          </Box>
          <Box sx={styles.fieldBlock}>
            <StyledPhoneInput
              placeholder="Phone number"
              defaultCountry="NL"
              continents={["EU", "OC"]}
              fullWidth
              required
              margin="dense"
              size="medium"
              error={errorFields.number}
              slotProps={{
                formHelperText: {
                  sx: {minHeight: HELPER_H}
                }
              }}
              helperText={errorFields.number ? "Enter the correct number (10-12 digits)" : " "}
              onChange={(e, value) =>
                handleChange("entity_phone_numbers", "number", e, value)
              }
              onBlur={() => handleBlur("number")}
              value={entityPhoneNumber?.number || ""}
            />
          </Box>
        </Box>
        <Button sx={styles.button} onClick={addNewPhoneNumber}>
          Add new phone number
        </Button>
        <Divider sx={{ marginTop: "15px", marginBottom: "15px" }} />
        {entityPhoneNumbersList?.length > 0 &&
          entityPhoneNumbersList.map(
            (
              item: {
                type: string;
                number: string;
              },
              index
            ) => {
              return editMode.isEdit && editMode.index === index ? (
                <Box sx={styles.editHolder} key={index}>
                  <Typography variant="body2" sx={styles.textField}>
                    Edit current phone number
                  </Typography>
                  <Box sx={styles.editFieldsBlockHolder}>
                    <Box sx={styles.fieldBlock}>
                      <LatinTextField
                        fullWidth
                        variant="standard"
                        label="Type"
                        type="text"
                        name="type"
                        helperText={" "}
                        slotProps={{
                          formHelperText: {
                            sx: {minHeight: HELPER_H}
                          }
                        }}
                        errorFields={errorEditFields.type}
                        setErrorFields={setErrorEditFields}
                        margin="dense"
                        value={editedPhoneNumberDetails?.type}

                        onChange={(e) => handleChangeItem("type", index, e, null)}
                      />

                      {/*<TextField*/}
                      {/*  sx={{ marginRight: "15px", width: "100%" }}*/}
                      {/*  label="Type"*/}
                      {/*  variant="standard"*/}
                      {/*  onChange={(e) => handleChangeItem("type", index, e)}*/}
                      {/*  value={editedPhoneNumberDetails.type}*/}
                      {/*/>*/}
                    </Box>
                    <Box sx={styles.fieldBlock}>
                      <StyledPhoneInput
                        fullWidth
                        defaultCountry="NL"
                        // sx={{ width: "100%" }}
                        label="Phone number"
                        continents={["EU", "OC"]}
                        name="phone_number"
                        variant="standard"
                        margin="dense"
                        required
                        error={errorEditFields.number}
                        slotProps={{
                          formHelperText: {
                            sx: {minHeight: HELPER_H}
                          }
                        }}
                        helperText={errorEditFields.number ? "Enter the correct number (10-12 digits)" : " "}
                        onChange={(e, value) => handleChangeItem("number", index, e, value)}
                        onBlur={() => handleBlurEdit("number")}
                        value={editedPhoneNumberDetails.number}
                      />
                    </Box>
                  </Box>
                  <Box sx={styles.editButtonGroup}>
                    <IconButton
                      type="button"
                      disabled={Object.values(errorEditFields).includes(true) }
                      // onMouseDown={(e) => {
                      //   e.preventDefault();
                      // }}
                      onClick={() => handleSetEditMode("edit", index)}
                    >
                      <DoneIcon fontSize={"small"} />
                    </IconButton>
                    <IconButton
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      onClick={() => handleSetEditMode("close", index)}
                    >
                      <CloseIcon fontSize={"small"} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box sx={styles.itemContainer} key={index}>
                  <Box sx={styles.itemHolder}>
                    <Box sx={styles.infoHolder}>
                      <Box sx={styles.typeInfo}>
                        <Typography sx={styles.titleDetails} variant={"h6"}>
                          Type:
                        </Typography>
                        <Tooltip
                          title={item?.type}
                          arrow
                          disableInteractive
                        >
                          <Typography sx={styles.itemDetails} variant={"h6"}>
                            {item?.type}
                          </Typography>
                        </Tooltip>
                      </Box>
                      <Box sx={styles.info}>
                        <Typography sx={styles.titleDetails} variant={"h6"}>
                          Phone number:
                        </Typography>
                        <Tooltip
                          title={item?.number}
                          arrow
                          disableInteractive
                        >
                          <Typography sx={styles.itemInfoDetails} variant={"h6"}>
                            {item?.number}
                          </Typography>
                        </Tooltip>

                      </Box>
                    </Box>
                    <Box sx={styles.iconHolder}>
                      <IconButton
                        aria-label="Edit"
                        onClick={() => handleSetEditMode("open", index)}
                      >
                        <EditIcon sx={styles.editIcon} />
                      </IconButton>
                      <IconButton
                        aria-label="Delete"
                        onClick={() => handleDeleteItem(index)}
                      >
                        <DeleteIcon sx={styles.deleteIcon} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              );
            }
          )}
      </AccordionDetails>
    </Accordion>
  );
});
export default EntityPhoneNumbersBlock;
